/**
 * One message, laid out like the composer it ends up in: a titled card with the subject on its own
 * row and the text below it (Sofia, Oct 15 2026: the InMail and follow-up boxes were hard to tell
 * apart from the page, and the subject did not line up with the text). The InMail and the
 * follow-up use the same card, so their edges, labels and text all share one left line.
 *
 * The text is `InlineLinkText` (Oct 22 2026): links show as LinkedIn shows them and are edited in
 * place. Native `<input>` for the subject on purpose — the documented deviation the propose form and Role
 * Prioritisation take: the DS Input/Textarea each draw their own floating-label box, and two such
 * boxes stacked inside a card cannot be made to line up with each other or with the card.
 */
import type { ReactNode } from 'react';
import { CARD_FRAME, CARD_HEAD, CARD_TITLE } from './messageBox';
import { InlineLinkText } from './InlineLinkText';

export function MessageCard({
  title,
  hint,
  subject,
  onSubject,
  body,
  onBody,
  disabled,
  minRows,
  assist,
  footer,
}: {
  title: string;
  hint?: ReactNode;
  subject: string;
  /** Leave out for a subject the recruiter cannot change (the follow-up's "Re: …"). */
  onSubject?: (v: string) => void;
  body: string;
  onBody: (v: string) => void;
  disabled?: boolean;
  minRows?: number;
  /** Inside the card, under the text: the ✨ rewrite bar. */
  assist?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className={`${CARD_FRAME} focus-within:border-[#0072EA] focus-within:shadow-[0_0_0_3px_#DBEBFC]`}>
        <div className={CARD_HEAD}>
          <span className={CARD_TITLE}>{title}</span>
          {hint && <span className="text-xs text-[#707070]">{hint}</span>}
        </div>
        <label className="flex items-center gap-3 border-b border-solid border-x-0 border-t-0 border-[#E9EAEC] px-4 py-2.5">
          <span className="w-14 shrink-0 text-xs text-[#707070]">Subject</span>
          {onSubject ? (
            <input
              aria-label={`${title} subject`}
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[14px] font-medium text-[#1A1A1A] outline-none disabled:text-[#707070]"
              value={subject}
              disabled={disabled}
              onChange={(e) => onSubject(e.target.value)}
            />
          ) : (
            <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-[#707070]">{subject || '—'}</span>
          )}
        </label>
        <InlineLinkText label={`${title} text`} value={body} onChange={onBody} disabled={disabled} minRows={minRows ?? 4} />
        {assist}
      </div>
      {footer}
    </div>
  );
}
